import { z } from 'zod';
import {
  conversationSourceStatusSchema,
  conversationSourceTypeSchema,
  pangeaEgressPolicySchema,
} from './chat';

/** Provider-neutral persistence envelope produced by an ingestion adapter. */
export const normalizedConversationSourceSchema = z
  .object({
    sourceType: conversationSourceTypeSchema,
    provider: z.string().min(1),
    externalConversationId: z.string().min(1),
    externalAgentId: z.string().min(1).nullable(),
    status: conversationSourceStatusSchema,
    egressPolicy: pangeaEgressPolicySchema.nullable(),
    metadata: z.record(z.string(), z.unknown()),
  })
  .strict();
export type NormalizedConversationSource = z.infer<typeof normalizedConversationSourceSchema>;

/**
 * Provider-neutral metadata attached to one source-imported message. These
 * fields preserve legacy read/egress semantics without storing a second
 * transcript or leaking a provider payload into the conversation model.
 */
export const sourceMessageMetadataSchema = z
  .object({
    interrupted: z.boolean().nullable().optional(),
    sourceMedium: z.string().nullable().optional(),
  })
  .strict();
export type SourceMessageMetadata = z.infer<typeof sourceMessageMetadataSchema>;

export interface NormalizedConversationSourceEnvelope {
  source: NormalizedConversationSource;
}

/**
 * Protocol implemented by provider-specific external-event codecs. It only
 * normalizes trusted ingestion payloads; model invocation and live voice
 * sessions use separate runtime capabilities. Selection is based on the trusted
 * event/source discriminator, not an environment-wide provider switch, so
 * several ingestion providers may coexist in the same environment.
 */
export interface ConversationIngestionAdapter<
  TRaw,
  TNormalized extends NormalizedConversationSource | NormalizedConversationSourceEnvelope =
    NormalizedConversationSource,
> {
  normalize(raw: TRaw): TNormalized;
}
